import { m } from "@trameria/i18n";
import { type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { RunPillsProps } from "../agent-chat/run-drawer-port";
import { HostedPillsContext } from "../shell/outside/composer-host";
import { DockInputContext } from "../shell/phone";
import { openOnPress } from "../ui/press";
import { BottomDrawer } from "./bottom-drawer";
import { usePhoneState } from "./context";
import { isTextEntry } from "./typing";

const PILL =
  "flex h-8 min-w-0 items-center gap-1.5 rounded-[16px] px-3 text-left text-[13px] text-body";

export function RunPills({
  device,
  deviceName,
  deviceDot,
  model,
  warn,
  readOnly,
  content,
}: RunPillsProps) {
  // In a post box a host draws (a native app): the pills go to it as data,
  // its press opens the same drawer here.
  const hosted = useContext(HostedPillsContext);
  const crowded =
    (useContext(DockInputContext)?.crowded ?? false) && hosted === null;
  const host = usePhoneState()?.overlayHost ?? null;
  const [open, setOpen] = useState(false);
  // Drawn once first opened, then kept (hidden while down).
  const [drawn, setDrawn] = useState(false);
  if (open && !drawn) setDrawn(true);
  const opener = useRef<HTMLButtonElement | null>(null);
  const deviceOnly = model === null || crowded;
  const name = deviceName ?? "";

  const show = (button: HTMLButtonElement | null) => {
    opener.current = button;
    const active = document.activeElement;
    if (active instanceof HTMLElement && isTextEntry(active)) active.blur();
    setOpen(true);
  };
  const hostedKey = hosted
    ? JSON.stringify([deviceName, deviceDot, model, warn, readOnly])
    : null;
  // Tells the host's box (an outside system) the pills' data; not a fetch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `hostedKey` stands for the data
  useEffect(() => {
    if (!hosted) return;
    const press = readOnly ? () => {} : () => show(null);
    const name = deviceName ?? "";
    hosted([
      {
        id: "device",
        label: name,
        dot: deviceDot,
        readAloud: (model === null && warn
          ? m.agents_conditions_open_device_warning
          : m.agents_conditions_open_device)({ device: name }),
        ...(model === null && warn ? { warn: true } : {}),
        onPress: press,
      },
      ...(model === null
        ? []
        : [
            {
              id: "model",
              label: model,
              readAloud: warn
                ? m.agents_conditions_open_warning()
                : m.agents_conditions_open(),
              ...(warn ? { warn: true } : {}),
              onPress: press,
            },
          ]),
    ]);
  }, [hosted, hostedKey]);
  // frontend-development: not a fetch; takes the pills back from the host's box.
  useEffect(() => (hosted ? () => hosted(null) : undefined), [hosted]);

  if (hosted)
    return drawn && host
      ? createPortal(
          <BottomDrawer
            open={open}
            label={m.agents_conditions_drawer()}
            closeLabel={m.agents_conditions_close()}
            onClose={() => setOpen(false)}
            opener={opener}
          >
            {content}
          </BottomDrawer>,
          host,
        )
      : null;

  if (readOnly)
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={`${PILL} flex-none max-w-[9rem]`}>
          <span className="sr-only">{m.agents_device_label()}</span>
          {device}
        </span>
        {model !== null && !crowded && (
          <span className={PILL}>
            <span className="sr-only">{m.agents_model_label()}</span>
            <span className="min-w-0 truncate">{model}</span>
          </span>
        )}
      </span>
    );

  const pill = (label: string, look: ReactNode, mark: boolean, cls = "") => (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      // A finger opens it at the press: by the click, iOS had taken the
      // focus at the press, the post box had folded and the click missed
      // the pill (ui/press.ts).
      {...openOnPress(show)}
      className={`${PILL} cursor-pointer bg-hover [-webkit-tap-highlight-color:transparent] active:bg-selected ${cls}`}
    >
      {look}
      {mark && <WarningMark />}
    </button>
  );
  return (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        {deviceOnly ? (
          pill(
            (warn
              ? m.agents_conditions_open_device_warning
              : m.agents_conditions_open_device)({ device: name }),
            device,
            warn,
          )
        ) : (
          <>
            {pill(
              m.agents_conditions_open_device({ device: name }),
              device,
              false,
              "flex-none max-w-[9rem]",
            )}
            {pill(
              warn
                ? m.agents_conditions_open_warning()
                : m.agents_conditions_open(),
              <span className="min-w-0 truncate">{model}</span>,
              warn,
            )}
          </>
        )}
      </span>
      {drawn &&
        host &&
        createPortal(
          <BottomDrawer
            open={open}
            label={m.agents_conditions_drawer()}
            closeLabel={m.agents_conditions_close()}
            onClose={() => setOpen(false)}
            opener={opener}
          >
            {content}
          </BottomDrawer>,
          host,
        )}
    </>
  );
}

/** "Something here needs a look": a round "!" (what it is, the drawer says). */
function WarningMark() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-[15px] w-[15px] flex-none items-center justify-center rounded-full bg-danger text-[10px] leading-none text-surface"
    >
      !
    </span>
  );
}
